import { PropsWithChildren, useMemo } from 'react';
import { ApolloProvider } from '@apollo/client';
import { DatabaseClientInterface } from 'shared/sql/DatabaseClientInterface';
import { createClient } from 'src/createClient.ts';
import { setResolvers } from 'src/database/setResolvers.ts';
import { UserSettings } from 'src/types/UserSettings.ts';

type Props = PropsWithChildren<{
    /**
     * A ready-to-use database client provided by the host application (e.g. the Stencil
     * web-app). It must already be initialized — this provider does not download or load
     * the offline database, it only wires local GraphQL resolvers on top of the client.
     */
    dbClient: DatabaseClientInterface;
    userSettings: UserSettings;
    uri: string;
}>;

/**
 * Drop-in alternative to DatabaseProvider for embedded usage. Instead of owning the offline
 * SQLite database (download, web workers, localForage cache), it builds the local-resolver
 * Apollo client around a DatabaseClient handed in by the host, so the host stays the single
 * owner of the database connection.
 */
export const EmbeddedDatabaseProvider = ({
    dbClient,
    userSettings,
    uri,
    children,
}: Props) => {
    const apollo = useMemo(() => {
        const client = createClient(`${uri}/graphql`);
        setResolvers(
            client,
            dbClient,
            userSettings.currentRegion,
            userSettings.currentLocale,
            userSettings.shoppingCenter ?? '',
        );
        return client;
    }, [dbClient, userSettings, uri]);

    return (
        <ApolloProvider client={apollo}>
            {children}
        </ApolloProvider>
    );
};
